/** Зарезервированное значение только для сетевой проекции, не для registry. */
export const HIDDEN_CARD_DEF_ID = '\u0000hidden-card';
/** Невозможное для авторитетного uint32 PRNG значение той же JSON-формы. */
export const REDACTED_RNG_STATE = -1;
function assertNever(event) {
    const type = event.type;
    throw new Error(`redactEvents: необработанный тип события "${String(type)}"`);
}
function hiddenInstance(instance, id, seq, revealDefinition = false) {
    return {
        id,
        seq,
        defId: revealDefinition ? instance.defId : HIDDEN_CARD_DEF_ID,
        ownerId: instance.ownerId,
        controllerId: instance.controllerId,
        zone: instance.zone,
        ...(instance.cellId !== undefined ? { cellId: instance.cellId } : {}),
        ...(instance.isToken === true ? { isToken: true } : {}),
        ...(instance.enteredRound !== undefined ? { enteredRound: instance.enteredRound } : {}),
        ...(instance.heat !== undefined ? { heat: instance.heat } : {}),
        ...(instance.freeze !== undefined ? { freeze: instance.freeze } : {}),
        stats: revealDefinition
            ? structuredClone(instance.stats)
            : { attack: 0, health: 0, maxHealth: 0 },
        vars: {},
    };
}
function hiddenAlias(side, zone, ordinal) {
    return `\u0000hidden:${side}:${zone}:${ordinal}`;
}
function canonicalHiddenOrder(state, ids) {
    return [...ids].sort((a, b) => {
        const aSeq = state.instances[a]?.seq ?? Number.MAX_SAFE_INTEGER;
        const bSeq = state.instances[b]?.seq ?? Number.MAX_SAFE_INTEGER;
        return aSeq - bSeq || (a < b ? -1 : a > b ? 1 : 0);
    });
}
/**
 * Чистая per-viewer проекция полного снимка. Обязательные поля CardInstance
 * нельзя физически удалить, не разрушив форму GameState, поэтому скрытые карты
 * получают явный неприменимый sentinel вместо реального определения/статов.
 */
export function redactFor(state, viewer) {
    if (!Object.hasOwn(state.players, viewer)) {
        throw new Error(`redactFor: неизвестный viewer "${viewer}"`);
    }
    const view = structuredClone(state);
    view.rng = { algo: 'mulberry32', state: REDACTED_RNG_STATE };
    if (view.pendingChoice !== undefined && view.pendingChoice.playerId !== viewer) {
        delete view.pendingChoice;
    }
    const remapZone = (player, zone, ids, revealDefinition = false) => ids.map((instanceId, ordinal) => {
        const instance = view.instances[instanceId];
        if (instance === undefined) {
            throw new Error(`redactFor: скрытый instance '${instanceId}' отсутствует`);
        }
        const alias = hiddenAlias(player.side, zone, ordinal);
        delete view.instances[instanceId];
        view.instances[alias] = hiddenInstance(instance, alias, ordinal + 1, revealDefinition);
        return alias;
    });
    for (const player of Object.values(view.players)) {
        // Порядок любой колоды скрыт. Состав оставшейся собственной колоды виден
        // владельцу, но opponent по-прежнему получает только opaque aliases/count.
        player.deck = remapZone(player, 'deck', canonicalHiddenOrder(state, player.deck), player.id === viewer);
        if (player.id !== viewer) {
            player.hand = remapZone(player, 'hand', player.hand);
            player.armedReactions = remapZone(player, 'armed', player.armedReactions);
            player.staged = [];
        }
    }
    return view;
}
/**
 * Безопасная проекция будущей event-дельты. MVP рассылает полные snapshots;
 * здесь консервативно удаляются private vars, чужие planning details и любые
 * события, которые позволили бы связать zone-scoped back с authoritative id.
 */
export function redactEvents(events, viewer, policy = {}) {
    if (typeof viewer !== 'string' || viewer === '') {
        throw new Error('redactEvents: viewer должен быть непустой строкой');
    }
    const safe = [];
    const sourceIndices = [];
    const pushSafe = (event, sourceIndex) => {
        safe.push(structuredClone(event));
        sourceIndices.push(sourceIndex);
    };
    for (const [sourceIndex, event] of events.entries()) {
        switch (event.type) {
            case 'CardPlanned':
            case 'CardUnplanned':
            case 'PlacementStaged':
            case 'PlacementUnstaged':
                if (event.playerId === viewer)
                    pushSafe(event, sourceIndex);
                break;
            case 'VarChanged':
                if (policy.canExposeVar?.(event) === true)
                    pushSafe(event, sourceIndex);
                break;
            case 'TokenCreated':
                if (event.cellId !== undefined || event.ownerId === viewer)
                    pushSafe(event, sourceIndex);
                break;
            case 'CardCreated':
                if (event.ownerId === viewer)
                    pushSafe(event, sourceIndex);
                break;
            case 'ReactionArmed':
                if (event.playerId === viewer)
                    pushSafe(event, sourceIndex);
                break;
            case 'CardDrawn':
                if (event.playerId === viewer)
                    pushSafe(event, sourceIndex);
                break;
            case 'CardMoved':
                // Скрытые переходы меняют zone-scoped alias только в следующем snapshot.
                // Authoritative id до первого публичного reveal наружу не выходит.
                if (!((event.from === 'deck' && event.to === 'hand')
                    || (event.from === 'hand' && event.to === 'armed'))) {
                    pushSafe(event, sourceIndex);
                }
                break;
            case 'PlayerReadyChanged':
            case 'ResourcesChanged':
            case 'EffectDurationChanged':
            case 'RoundStarted':
            case 'RoundEnded':
            case 'PhaseChanged':
            case 'CardRevealed':
            case 'CardResolved':
            case 'CardFizzled':
            case 'ReactionTriggered':
            case 'UnitPlaced':
            case 'UnitEnteredPlay':
            case 'UnitAppeared':
            case 'FieldRevealCompleted':
            case 'HeatChanged':
            case 'FreezeChanged':
            case 'ShieldAbsorbed':
            case 'ShieldDepleted':
            case 'AttackAvoidanceChecked':
            case 'AttackResolved':
            case 'AttackPerformed':
            case 'UnitMoved':
            case 'AttachmentAdded':
            case 'AttachmentTriggered':
            case 'AttachmentRemoved':
            case 'CaptureApplied':
            case 'CaptureReturned':
            case 'CaptureFizzled':
            case 'ExtraAttackQueued':
            case 'ExtraAttackResolved':
            case 'CombatStarted':
            case 'DamageDealt':
            case 'PlayerHealthRestored':
            case 'UnitHealed':
            case 'HackApplied':
            case 'HackTriggered':
            case 'HackRemoved':
            case 'StatChanged':
            case 'EffectAdded':
            case 'EffectCounterChanged':
            case 'EffectRemoved':
            case 'UnitDied':
            case 'TokenRemoved':
            case 'TokenCreationSkipped':
            case 'CardConsumed':
            case 'CardsAbsorbed':
            case 'CardTransformed':
            case 'AssemblyCompleted':
            case 'TemporaryEnergyGranted':
            case 'TemporaryEnergyExpired':
            case 'TimedDestroyScheduled':
            case 'TimedDestroyResolved':
            case 'RuleChanged':
            case 'RandomDamageReplacementResolved':
            case 'RandomUnitSelectionResolved':
            case 'CombatEnded':
            case 'MatchEnded':
                pushSafe(event, sourceIndex);
                break;
            default:
                assertNever(event);
        }
    }
    // A causal edge points to the authoritative event array. Redaction can omit
    // private entries, so remap it to the public compact batch; if the parent is
    // not public, omit the edge rather than exposing its timing or identity.
    const publicIndexBySource = new Map(sourceIndices.map((source, index) => [source, index]));
    return safe.map((event) => {
        if (event.causedByEventIndex === undefined)
            return event;
        const publicIndex = publicIndexBySource.get(event.causedByEventIndex);
        if (publicIndex !== undefined)
            return { ...event, causedByEventIndex: publicIndex };
        const { causedByEventIndex: ignored, ...withoutCause } = event;
        void ignored;
        return withoutCause;
    });
}
//# sourceMappingURL=redact.js.map